// ============================================================
// The chips (v8.363.1): Vox — all; the groups I am in (the GM: those
// that listen to them); Vox — Players for a player; then one chip per
// person I have whispered with, unless I closed it after their last
// line. Pure.
// ============================================================
import type { ChatChannel, ChatMessage, ConversationKey, Person } from './types';
import { conversationOf } from './audience';

export interface Conversation {
  key: ConversationKey;
  label: string;
  /** The newest line's timestamp, 0 when none. */
  lastTs: number;
  /** DM chips close; the rest stay. */
  closable: boolean;
}

export interface ConversationInput {
  messages: ChatMessage[];
  channels: ChatChannel[];
  me: string;
  isGm: boolean;
  people: Person[];
  /** DMs opened by hand (a click on a name) that may hold no line yet. */
  openDms: string[];
  /** When each DM chip was closed; a newer line from them reopens it. */
  closedDmAt: Record<string, number>;
}

function labelFor(key: ConversationKey, channels: ChatChannel[], people: Person[]): string {
  if (key === 'table') return 'Vox — all';
  if (key === 'players') return 'Vox — Players';
  if (key.startsWith('channel:')) {
    const ch = channels.find((c) => c.id === key.slice('channel:'.length));
    return `Vox — ${ch?.name ?? 'group'}`;
  }
  const uid = key.slice('dm:'.length);
  const p = people.find((x) => x.uid === uid);
  return `Private vox to ${p?.name ?? 'someone'}`;
}

export function deriveConversations(input: ConversationInput): Conversation[] {
  const { messages, channels, me, isGm, people } = input;
  const lastBy = latestByConversation(messages, me);
  const entry = (key: ConversationKey, closable = false): Conversation => ({
    key,
    label: labelFor(key, channels, people),
    lastTs: lastBy.get(key) ?? 0,
    closable,
  });
  return [
    entry('table'),
    ...channels.filter((ch) => listensTo(ch, me, isGm)).map((ch) => entry(`channel:${ch.id}`)),
    ...(isGm ? [] : [entry('players')]),
    ...openDmKeys(input, lastBy).map((key) => entry(key, true)),
  ];
}

/** When each conversation last spoke. */
function latestByConversation(messages: ConversationInput['messages'], me: string): Map<ConversationKey, number> {
  const lastBy = new Map<ConversationKey, number>();
  for (const m of messages) {
    const key = conversationOf(m, me);
    lastBy.set(key, Math.max(lastBy.get(key) ?? 0, m.ts));
  }
  return lastBy;
}

/** A member hears a channel; the GM also hears the ones set to let them listen. */
function listensTo(ch: ConversationInput['channels'][number], me: string, isGm: boolean): boolean {
  return ch.members.includes(me) || (isGm && !!ch.gmListens);
}

/**
 * The DMs to list: the ones held open, plus any that has spoken since it
 * was closed - newest first, then by uid.
 */
function openDmKeys(input: ConversationInput, lastBy: Map<ConversationKey, number>): ConversationKey[] {
  const dmUids = new Set<string>(input.openDms);
  for (const [key, ts] of lastBy) {
    const uid = reopenedDm(key, ts, input);
    if (uid !== null) dmUids.add(uid);
  }
  return [...dmUids]
    .map((uid) => ({ key: `dm:${uid}` as ConversationKey, uid }))
    .sort((a, b) => (lastBy.get(b.key) ?? 0) - (lastBy.get(a.key) ?? 0) || a.uid.localeCompare(b.uid))
    .map((dm) => dm.key);
}

/** The other person of a DM that has spoken since it was closed, else null. */
function reopenedDm(key: ConversationKey, ts: number, { me, closedDmAt }: ConversationInput): string | null {
  if (!key.startsWith('dm:')) return null;
  const uid = key.slice('dm:'.length);
  if (uid === me || !(ts > (closedDmAt[uid] ?? 0))) return null;
  return uid;
}
